export interface LojaImportacao {
  id: number;
  nome: string;
  catalogoId: number;
}

export interface CadastroImportacao {
  tipo: string;
  objeto?: string;
  idOrigem: number;
  idDestino: number | null;
  nome: string;
  acao: 'criar' | 'reutilizar';
  avisos: string[];
}

export interface ResumoImportacao {
  tipo: string;
  criar: number;
  reutilizar: number;
}

export interface OpcaoAdicionalImportacao {
  idOrigem: number;
  codigoPdv: string | null;
  nome: string;
  descricao: string | null;
  valor: number | null;
  disponivel: boolean;
  oculta: boolean;
}

export interface AdicionalImportacao {
  idOrigem: number;
  codigoPdv: string | null;
  nome: string;
  obrigatorio: boolean;
  oculto: boolean;
  qtdMinima: number | null;
  qtdMaxima: number | null;
  opcoes: OpcaoAdicionalImportacao[];
}

export interface ProdutoImportacao {
  idOrigem: number;
  nome: string;
  codigoPdv: string;
  preco: number;
  precoExibicao: number;
  aPartirDe: boolean;
  adicionais: AdicionalImportacao[];
  imagem: string | null;
  categoria: {idOrigem: number; nome: string} | null;
  status: 'novo' | 'bloqueado' | 'jaExiste';
  motivo: string | null;
  cadastros: CadastroImportacao[];
}

export interface PreviaImportacao {
  produtos: ProdutoImportacao[];
  totais: {disponiveis: number; bloqueados: number; existentesOmitidos: number};
}

export interface RevisaoImportacao {
  produtos: ProdutoImportacao[];
  podeImportar: boolean;
  cadastros: CadastroImportacao[];
  resumo: ResumoImportacao[];
  avisos: string[];
}

export interface RetornoProdutoImportacao {
  status: 'importado' | 'jaExiste';
  idOrigem: number;
  idDestino: number;
  nome: string;
  cadastros: CadastroImportacao[];
  resumo: ResumoImportacao[];
  avisos: string[];
}

export interface ApiImportacaoEntreLojas {
  listeLojas(): Promise<LojaImportacao[]>;
  obtenhaPrevia(idOrigem: number): Promise<PreviaImportacao>;
  revise(idOrigem: number, idsProdutos: number[]): Promise<RevisaoImportacao>;
  importeProduto(idOrigem: number, idProduto: number): Promise<RetornoProdutoImportacao>;
}

export interface ResultadoProdutoImportacao {
  produto: ProdutoImportacao;
  status: 'pendente' | 'importando' | 'importado' | 'jaExiste' | 'falhou';
  erro?: string;
  retorno?: RetornoProdutoImportacao;
}

export function mensagemErroImportacao(erro: any): string {
  return typeof erro === 'string' ? erro : erro?.message || 'Não foi possível concluir esta operação. Tente novamente.';
}

const normalize = (valor: string): string => (valor || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');

// Estado do assistente sem dependências Angular ou do servidor.
export class FluxoImportacaoEntreLojas {
  passo = 1;
  readonly passos = ['Loja de origem', 'Selecionar produtos', 'Revisar importação', 'Resultado'];
  lojas: LojaImportacao[] = [];
  loja: LojaImportacao | null = null;
  produtos: ProdutoImportacao[] = [];
  totais = {disponiveis: 0, bloqueados: 0, existentesOmitidos: 0};
  selecao = new Set<number>();
  adicionaisAbertos = new Set<number>();
  revisao: RevisaoImportacao | null = null;
  resultados: ResultadoProdutoImportacao[] = [];
  buscaLoja = '';
  buscaProduto = '';
  categoriaFiltro: number | null = null;
  carregando = false;
  importando = false;
  erro = '';
  encerrado = false;

  constructor(private api: ApiImportacaoEntreLojas) {}

  get ocupado(): boolean { return this.carregando || this.importando; }
  get lojasFiltradas(): LojaImportacao[] {
    return this.lojas.filter(item => normalize(item.nome).includes(normalize(this.buscaLoja)));
  }
  get categorias(): Array<{id: number; nome: string}> {
    return Array.from(new Map(this.produtos.map(item => [item.categoria?.idOrigem || 0,
      {id: item.categoria?.idOrigem || 0, nome: item.categoria?.nome || 'Sem categoria'}])).values());
  }
  get produtosFiltrados(): ProdutoImportacao[] {
    const termo = normalize(this.buscaProduto);
    return this.produtos.filter(item => (this.categoriaFiltro === null || (item.categoria?.idOrigem || 0) === this.categoriaFiltro) &&
      normalize(`${item.nome} ${item.codigoPdv || ''}`).includes(termo));
  }
  get gruposProdutos(): Array<{id: number; nome: string; produtos: ProdutoImportacao[]}> {
    const produtos = this.produtosFiltrados;
    return this.categorias.map(item => ({...item, produtos: produtos.filter(produto => (produto.categoria?.idOrigem || 0) === item.id)}))
      .filter(item => item.produtos.length > 0);
  }
  get selecionados(): ProdutoImportacao[] { return this.produtos.filter(item => this.selecao.has(item.idOrigem)); }
  get importados(): number { return this.resultados.filter(item => item.status === 'importado').length; }
  get existentes(): number { return this.resultados.filter(item => item.status === 'jaExiste').length; }
  get falhas(): number { return this.resultados.filter(item => item.status === 'falhou').length; }
  get processados(): number { return this.importados + this.existentes + this.falhas; }
  get progresso(): number { return this.resultados.length ? Math.round(this.processados * 100 / this.resultados.length) : 0; }
  get produtoAtual(): string { return this.resultados.find(item => item.status === 'importando')?.produto.nome || ''; }

  async carregueLojas(): Promise<void> {
    if(this.ocupado || this.encerrado) return;
    this.carregando = true; this.erro = '';
    try { this.lojas = (await this.api.listeLojas()).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR')); }
    catch(erro) { this.erro = mensagemErroImportacao(erro); }
    finally { this.carregando = false; }
  }

  selecioneLoja(loja: LojaImportacao): void {
    if(this.ocupado || this.encerrado || this.passo !== 1 || this.loja?.id === loja.id) return;
    this.loja = loja; this.produtos = []; this.selecao.clear(); this.revisao = null;
    this.adicionaisAbertos.clear();
    this.buscaProduto = ''; this.categoriaFiltro = null; this.erro = '';
  }

  async carregueProdutos(): Promise<void> {
    if(!this.loja || this.ocupado || this.encerrado || this.passo !== 1) return;
    this.carregando = true; this.erro = '';
    try {
      const previa = await this.api.obtenhaPrevia(this.loja.id);
      if(this.encerrado) return;
      this.produtos = previa.produtos.filter(item => item.status !== 'jaExiste');
      this.adicionaisAbertos.clear();
      this.selecao = new Set(this.produtos.filter(item => item.status === 'novo' && this.selecao.has(item.idOrigem)).map(item => item.idOrigem));
      this.revisao = null;
      this.totais = previa.totais; this.passo = 2;
    } catch(erro) { this.erro = mensagemErroImportacao(erro); }
    finally { this.carregando = false; }
  }

  selecioneProduto(produto: ProdutoImportacao, marcado: boolean): void {
    if(this.ocupado || this.encerrado || this.passo !== 2 || produto.status !== 'novo') return;
    if(marcado) this.selecao.add(produto.idOrigem); else this.selecao.delete(produto.idOrigem);
    this.revisao = null;
  }

  alterneAdicionais(produto: ProdutoImportacao): void {
    if(this.ocupado || this.encerrado || this.passo !== 2 || !produto.adicionais?.length) return;
    if(this.adicionaisAbertos.has(produto.idOrigem)) this.adicionaisAbertos.delete(produto.idOrigem);
    else this.adicionaisAbertos.add(produto.idOrigem);
  }

  todosSelecionados(produtos: ProdutoImportacao[]): boolean {
    const disponiveis = produtos.filter(item => item.status === 'novo');
    return disponiveis.length > 0 && disponiveis.every(item => this.selecao.has(item.idOrigem));
  }

  possuiDisponiveis(produtos: ProdutoImportacao[]): boolean {
    return produtos.some(item => item.status === 'novo');
  }

  parcialmenteSelecionados(produtos: ProdutoImportacao[]): boolean {
    return produtos.some(item => this.selecao.has(item.idOrigem)) && !this.todosSelecionados(produtos);
  }

  selecioneLista(produtos: ProdutoImportacao[], marcado: boolean): void {
    produtos.forEach(item => this.selecioneProduto(item, marcado));
  }

  async reviseSelecao(): Promise<void> {
    if(!this.loja || !this.selecao.size || this.ocupado || this.encerrado || this.passo !== 2) return;
    this.carregando = true; this.erro = '';
    try {
      this.revisao = await this.api.revise(this.loja.id, this.selecionados.map(item => item.idOrigem));
      if(this.encerrado) return;
      for(const produto of this.revisao.produtos) {
        const indice = this.produtos.findIndex(item => item.idOrigem === produto.idOrigem);
        if(indice >= 0) this.produtos[indice] = produto;
      }
      this.passo = 3;
    } catch(erro) { this.erro = mensagemErroImportacao(erro); }
    finally { this.carregando = false; }
  }

  volte(): void {
    if(this.ocupado || this.encerrado || this.passo < 2 || this.passo > 3) return;
    if(this.passo === 3) {
      this.produtos.filter(item => item.status !== 'novo').forEach(item => this.selecao.delete(item.idOrigem));
      this.totais.existentesOmitidos += this.produtos.filter(item => item.status === 'jaExiste').length;
      this.produtos = this.produtos.filter(item => item.status !== 'jaExiste');
      this.totais.disponiveis = this.produtos.filter(item => item.status === 'novo').length;
      this.totais.bloqueados = this.produtos.filter(item => item.status === 'bloqueado').length;
      this.revisao = null;
    }
    this.passo--; this.erro = '';
  }

  async importe(): Promise<void> {
    if(this.ocupado || this.encerrado || this.passo !== 3 || !this.loja || !this.revisao?.podeImportar || !this.revisao.produtos.length) return;
    this.resultados = this.revisao.produtos.map(produto => ({produto, status: 'pendente'}));
    this.passo = 4;
    await this.executeFila(this.resultados);
  }

  async tenteFalhasNovamente(): Promise<void> {
    if(this.ocupado || this.encerrado || this.passo !== 4 || !this.loja) return;
    const falhas = this.resultados.filter(item => item.status === 'falhou');
    if(!falhas.length) return;
    falhas.forEach(item => {item.status = 'pendente'; item.erro = '';});
    await this.executeFila(falhas);
  }

  private async executeFila(fila: ResultadoProdutoImportacao[]): Promise<void> {
    this.importando = true; this.erro = '';
    try {
      for(const item of fila) {
        if(this.encerrado) break;
        item.status = 'importando';
        try {
          const retorno = await this.api.importeProduto(this.loja.id, item.produto.idOrigem);
          if(!['importado', 'jaExiste'].includes(retorno?.status) || !retorno.idDestino || retorno.idOrigem !== item.produto.idOrigem)
            throw new Error('O servidor retornou um resultado inválido. Repita este produto para verificar a importação.');
          item.retorno = retorno; item.status = retorno.status;
        } catch(erro) { item.status = 'falhou'; item.erro = mensagemErroImportacao(erro); }
      }
    } finally { this.importando = false; }
  }
}
